import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import { KeyboardArrowDownRounded } from '@mui/icons-material';

const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <KeyboardArrowDownRounded sx={{ fontSize: 24 }} />
  </components.DropdownIndicator>
);

const IndicatorSeparator = () => null;

const buildStyles = ({ hasLeadingIcon, isDarkMode, minHeight, menuZIndex }) => {
  const colors = isDarkMode
    ? {
        control: '#1C2434',
        border: '#3D4A5C',
        hoverBorder: '#64748B',
        focusRing: 'rgba(96, 165, 250, 0.22)',
        text: '#F1F5F9',
        placeholder: '#94A3B8',
        menu: '#1C2434',
        selected: '#1E3A8A',
        focused: '#273449',
        shadow: '0 18px 50px rgba(0, 0, 0, 0.32)'
      }
    : {
        control: '#FFFFFF',
        border: '#CBD5E1',
        hoverBorder: '#94A3B8',
        focusRing: 'rgba(60, 80, 224, 0.10)',
        text: '#0F172A',
        placeholder: '#94A3B8',
        menu: '#FFFFFF',
        selected: '#E0E7FF',
        focused: '#F8FAFC',
        shadow: '0 18px 50px rgba(15, 23, 42, 0.16)'
      };

  return {
    control: (base, state) => ({
      ...base,
      minHeight,
      borderRadius: 16,
      borderColor: state.isFocused ? '#3C50E0' : colors.border,
      boxShadow: state.isFocused
        ? `0 0 0 4px ${colors.focusRing}`
        : '0 8px 20px rgba(15, 23, 42, 0.05)',
      backgroundColor: colors.control,
      transition: 'all 160ms ease',
      '&:hover': {
        borderColor: state.isFocused ? '#3C50E0' : colors.hoverBorder
      }
    }),
    valueContainer: base => ({
      ...base,
      paddingLeft: hasLeadingIcon ? 44 : 14,
      paddingRight: 6,
      minHeight
    }),
    placeholder: base => ({
      ...base,
      color: colors.placeholder
    }),
    singleValue: base => ({
      ...base,
      color: colors.text,
      fontWeight: 600
    }),
    input: base => ({
      ...base,
      color: colors.text
    }),
    menu: base => ({
      ...base,
      zIndex: menuZIndex,
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: colors.shadow,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.menu
    }),
    menuList: base => ({
      ...base,
      paddingTop: 8,
      paddingBottom: 8,
      backgroundColor: colors.menu
    }),
    option: (base, state) => ({
      ...base,
      margin: '0 8px',
      borderRadius: 12,
      padding: '12px 14px',
      backgroundColor: state.isSelected
        ? colors.selected
        : state.isFocused
          ? colors.focused
          : colors.menu,
      color: colors.text,
      cursor: 'pointer',
      fontWeight: state.isSelected ? 700 : 500
    }),
    dropdownIndicator: base => ({
      ...base,
      color: colors.placeholder,
      paddingRight: 14
    }),
    clearIndicator: base => ({
      ...base,
      color: colors.placeholder
    }),
    menuPortal: base => ({
      ...base,
      zIndex: menuZIndex
    })
  };
};

const BrandedSelect = ({
  label,
  helperText,
  value,
  onChange,
  options,
  placeholder,
  leadingIcon: LeadingIcon,
  isDisabled,
  minHeight,
  className,
  menuZIndex,
  usePortal,
  maxMenuHeight,
  menuPlacement,
  isSearchable,
  noOptionsMessage,
  isDarkMode
}) => {
  const selectedOption = useMemo(
    () => options.find(option => option.value === value) || null,
    [options, value]
  );

  const selectStyles = useMemo(
    () =>
      buildStyles({
        hasLeadingIcon: Boolean(LeadingIcon),
        isDarkMode,
        minHeight,
        menuZIndex
      }),
    [LeadingIcon, isDarkMode, minHeight, menuZIndex]
  );

  return (
    <div className={className}>
      {label ? (
        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
          {label}
        </label>
      ) : null}
      {helperText ? (
        <p className="mb-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {helperText}
        </p>
      ) : null}

      <div className="relative">
        {LeadingIcon ? (
          <span className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-slate-400">
            <LeadingIcon sx={{ fontSize: 22 }} />
          </span>
        ) : null}

        <Select
          classNamePrefix="branded-select"
          options={options}
          value={selectedOption}
          onChange={selected => onChange(selected?.value || '')}
          isSearchable={isSearchable}
          isDisabled={isDisabled}
          placeholder={placeholder}
          styles={selectStyles}
          components={{
            DropdownIndicator,
            IndicatorSeparator
          }}
          maxMenuHeight={maxMenuHeight}
          menuPlacement={menuPlacement}
          menuPortalTarget={
            usePortal && typeof document !== 'undefined'
              ? document.body
              : undefined
          }
          noOptionsMessage={noOptionsMessage}
        />
      </div>
    </div>
  );
};

BrandedSelect.propTypes = {
  label: PropTypes.string,
  helperText: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  leadingIcon: PropTypes.elementType,
  isDisabled: PropTypes.bool,
  minHeight: PropTypes.number,
  className: PropTypes.string,
  menuZIndex: PropTypes.number,
  usePortal: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  menuPlacement: PropTypes.oneOf(['auto', 'top', 'bottom']),
  isSearchable: PropTypes.bool,
  noOptionsMessage: PropTypes.func,
  isDarkMode: PropTypes.bool
};

BrandedSelect.defaultProps = {
  label: '',
  helperText: '',
  placeholder: '',
  leadingIcon: null,
  isDisabled: false,
  minHeight: 56,
  className: '',
  menuZIndex: 9999,
  usePortal: true,
  maxMenuHeight: 280,
  menuPlacement: 'auto',
  isSearchable: false,
  noOptionsMessage: undefined,
  isDarkMode: false
};

export default BrandedSelect;
